/**
 * Client-side check for disposable and anonymous email domains.
 *
 * This is for immediate feedback only - it saves the user a round trip and a
 * spent reCAPTCHA token. The backend holds the authoritative list (config/
 * disposable_email_domains.txt, ~8300 domains) and rejects anything this misses,
 * so the two do not have to agree. Shipping all 8300 domains to the browser
 * would add ~120KB to the bundle to catch cases the server already catches.
 *
 * Keep this to the providers people actually try.
 */

const BLOCKED_DOMAINS = new Set([
    // disposable / throwaway
    '10minutemail.com', '20minutemail.com', 'anonaddy.com', 'burnermail.io',
    'dispostable.com', 'emailondeck.com', 'fakeinbox.com', 'getairmail.com',
    'getnada.com', 'guerrillamail.com', 'guerrillamail.net', 'guerrillamail.org',
    'inboxbear.com', 'mailcatch.com', 'maildrop.cc', 'mailinator.com',
    'mailnesia.com', 'mailsac.com', 'mintemail.com', 'mohmal.com',
    'moakt.com', 'mytemp.email', 'nowmymail.com', 'sharklasers.com',
    'spam4.me', 'temp-mail.org', 'tempail.com', 'tempinbox.com',
    'tempmail.com', 'tempmail.net', 'tempmailo.com', 'tempr.email',
    'throwawaymail.com', 'trashmail.com', 'trashmail.de', 'yopmail.com',
    'yopmail.fr', 'yopmail.net', 'discard.email', 'dropmail.me',
    'fakemail.net', 'linshiyouxiang.net', 'mailtemp.top', 'minuteinbox.com',
    'tmpmail.org', 'vomoto.com', 'wegwerfemail.de', 'grr.la',
    'inboxkitten.com', 'harakirimail.com',

    // anonymous / privacy-focused providers
    'protonmail.com', 'protonmail.ch', 'proton.me', 'pm.me',
    'tutanota.com', 'tutanota.de', 'tutamail.com', 'tuta.io', 'tuta.com',
    'keemail.me', 'mailfence.com', 'hushmail.com', 'riseup.net',
    'disroot.org', 'cock.li', 'danwin1210.de',
]);

/** The domain part of an address, lowercased, or null if there isn't exactly one. */
export const domainOf = (email) => {
    if (typeof email !== 'string') return null;
    const parts = email.split('@');
    if (parts.length !== 2) return null;
    const domain = parts[1].trim().toLowerCase().replace(/\.+$/, '');
    return domain || null;
};

/**
 * True for a blocked domain. Parent domains match, so one 'mailinator.com'
 * entry also covers 'team.mailinator.com' - same rule as the backend.
 */
export const isBlockedEmailDomain = (email) => {
    const domain = domainOf(email);
    if (!domain) return false;
    if (BLOCKED_DOMAINS.has(domain)) return true;

    const parts = domain.split('.');
    for (let i = 1; i < parts.length - 1; i++) {
        if (BLOCKED_DOMAINS.has(parts.slice(i).join('.'))) return true;
    }
    return false;
};

export const BLOCKED_EMAIL_MESSAGE =
    'Disposable and anonymous email addresses are not accepted. '
    + 'Please register with your work or institutional email address.';
